import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight, TrendingDown, TrendingUp } from "lucide-react";
import type { FC } from "react";
import { DashboardDetailRowIcon } from "web_myplann/Dashboard/Components/DashboardDetailRowIcon";
import { DashboardRowButton } from "web_myplann/Dashboard/Components/DashboardRowButton";
import type { DashboardDetailRowData } from "web_myplann/Dashboard/Utils/dashboardDetailRows";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";

type Props = {
	row: DashboardDetailRowData;
	isLast: boolean;
	onClick: () => void;
};

export const DashboardDetailRow: FC<Props> = ({ row, isLast, onClick }) => {
	const { palette, iconSize, shape } = useTheme();

	const showChange = row.change != null && row.change !== 0;
	const isPositive = (row.change ?? 0) > 0;

	return (
		<DashboardRowButton onClick={onClick} showDivider={!isLast}>
			<DashboardDetailRowIcon
				icon={row.icon}
				iconText={row.iconText}
				iconColor={row.iconColor}
			/>

			<Stack flex={1} minWidth={0}>
				<Typography
					variant="bodyStrong"
					overflow="hidden"
					whiteSpace="nowrap"
					textOverflow="ellipsis"
				>
					{row.name}
				</Typography>
				{row.subtitle && (
					<Typography
						variant="caption"
						color={palette.grey[500]}
						overflow="hidden"
						whiteSpace="nowrap"
						textOverflow="ellipsis"
					>
						{row.subtitle}
					</Typography>
				)}
				{row.extra && (
					<Typography variant="hint" mt={0.2}>
						{row.extra}
					</Typography>
				)}
			</Stack>

			{row.valueText && (
				<Stack alignItems="flex-end" flexShrink={0}>
					<Typography variant="bodyStrong" tabular>
						{row.valueText}{" "}
						<Typography
							component="span"
							variant="caption"
							sx={{ opacity: 0.7 }}
						>
							{row.currencySign}
						</Typography>
					</Typography>
					{showChange && (
						<Stack
							direction="row"
							alignItems="center"
							gap={0.4}
							mt={0.2}
							px={1}
							py={0.2}
							borderRadius={shape.pillRadius}
							bgcolor={isPositive ? palette.green[100] : palette.red[100]}
						>
							{isPositive ? (
								<TrendingUp size={10} color={palette.green[700]} />
							) : (
								<TrendingDown size={10} color={palette.red[700]} />
							)}
							<PercentageDelta
								change={row.change}
								fontSize={12}
								fontWeight={500}
								color={isPositive ? palette.green[700] : palette.red[700]}
							/>
						</Stack>
					)}
				</Stack>
			)}

			<Box flexShrink={0} display="inline-flex">
				<ChevronRight size={iconSize.sm} color={palette.grey[400]} />
			</Box>
		</DashboardRowButton>
	);
};
